import type { OBB, Vec2 } from '../../../geo/types';
import { decodeBase64 } from '../../../core/base64';
import committed from '../../../data/woodcutGround.json';

/**
 * What Cornelis Anthonisz's woodcut of 1544 draws on a piece of ground: nothing read (outside the
 * walls), open ground (a street, a yard, a garden, a court, water), a building, or ground a
 * building drawn before it hides.
 */
export type GroundState = 'unread' | 'open' | 'built' | 'hidden';

const STATES: readonly GroundState[] = ['unread', 'open', 'built', 'hidden'];

export interface WoodcutGroundFile {
  readonly source: string;
  readonly image: string;
  readonly method: string;
  /** The south-west corner of the grid, in layout metres. */
  readonly origin: readonly number[];
  readonly cell: number;
  readonly cols: number;
  readonly rows: number;
  readonly states: readonly string[];
  /** Two bits a cell, four cells a byte from its low bits, rows counted north from the origin. */
  readonly cells: string;
  /** One bit a cell from each byte's low bit, in the cells' order: the built ground surely built on. */
  readonly sure: string;
}

/**
 * PLAN Lots, the woodcut's ground (2026-10-05): the coloured facsimile of the 1544 woodcut, carried
 * to layout metres by matching its canals and streets to the layout's (tools/woodcut,
 * docs/woodcut-ground.md), says where buildings stand and where the ground lies open. A building
 * behind the houses keeps off ground shown open beyond `rearOpen` of it. A plot is built on where
 * the woodcut roofs `plotBuilt` of it, and roofed through where it roofs `through`. Ground read on
 * less than `read` of a building says nothing of it. The city wall, its towers and gates are left
 * out of the built ground: the planner reserves them itself. The view looks down on the city, so a
 * building covers the ground behind it for its height: built ground is read for a low building's
 * wall, and only the part a gabled house's ridge could not cover is surely built on.
 */
export const GROUND = {
  rearOpen: 0.35,
  plotBuilt: 0.5,
  through: 0.85,
  read: 0.5,
} as const;

export interface GroundShares {
  /** The share of the building's ground the woodcut was read on. */
  readonly read: number;
  readonly open: number;
  readonly built: number;
}

export interface WoodcutGround {
  readonly grid: {
    readonly origin: Vec2;
    readonly cell: number;
    readonly cols: number;
    readonly rows: number;
  };
  at(p: Vec2): GroundState;
  /** The state of the cell `col` east and `row` north of the origin's. */
  stateOf(col: number, row: number): GroundState;
  /** Whether the cell is built ground no gabled house drawn before it could have covered. */
  surelyBuilt(col: number, row: number): boolean;
  centreOf(col: number, row: number): Vec2;
  shares(obb: OBB): GroundShares;
  counts(): Readonly<Record<GroundState, number>>;
  /** Whether the woodcut was read on enough of a building to speak of it. */
  isRead(obb: OBB): boolean;
  /** Whether a building behind the houses would stand on ground shown open. */
  showsOpen(obb: OBB): boolean;
  /** Whether the woodcut roofs a plot. */
  showsBuilt(obb: OBB): boolean;
  /** Whether the woodcut roofs a plot from end to end. */
  roofedThrough(obb: OBB): boolean;
}

function fail(message: string): never {
  throw new Error(`woodcutGround.json: ${message}`);
}

function isSize(v: unknown): v is number {
  return Number.isInteger(v) && (v as number) > 0;
}

function unpack(file: WoodcutGroundFile): Uint8Array {
  const packed = decodeBase64(file.cells);
  const count = file.cols * file.rows;
  if (packed.length !== Math.ceil(count / 4)) fail('the cells do not fill the grid');
  const cells = new Uint8Array(count);
  for (let i = 0; i < count; i++) cells[i] = (packed[i >> 2]! >> ((i & 3) * 2)) & 3;
  return cells;
}

function unpackSure(file: WoodcutGroundFile, cells: Uint8Array): Uint8Array {
  if (typeof file.sure !== 'string') fail('no sure ground');
  const packed = decodeBase64(file.sure);
  if (packed.length !== Math.ceil(cells.length / 8)) fail('the sure ground does not fill the grid');
  const BUILT = STATES.indexOf('built');
  return cells.map((state, i) => (state === BUILT ? (packed[i >> 3]! >> (i & 7)) & 1 : 0));
}

/** The reading file, checked: produced by tools/woodcut, and bundled by the build. */
export function parseWoodcutGround(value: unknown): WoodcutGround {
  const file = value as Partial<WoodcutGroundFile> | null;
  if (file === null || typeof file !== 'object') fail('not a reading');
  const { origin, cell, cols, rows, states, cells: text } = file;
  if (!Array.isArray(origin) || origin.length !== 2 || !origin.every(Number.isFinite))
    fail('no origin');
  if (typeof cell !== 'number' || !(cell > 0)) fail('no cell size');
  if (!isSize(cols) || !isSize(rows)) fail('no grid size');
  if (!Array.isArray(states) || states.join() !== STATES.join()) fail('unknown cell states');
  if (typeof text !== 'string') fail('no cells');
  const [x0, y0] = origin as [number, number];
  const cells = unpack(file as WoodcutGroundFile);
  const sure = unpackSure(file as WoodcutGroundFile, cells);
  const [UNREAD, OPEN, BUILT] = [0, 1, 2];

  const indexAt = (x: number, y: number): number => {
    const c = Math.floor((x - x0) / cell);
    const r = Math.floor((y - y0) / cell);
    return c < 0 || c >= cols || r < 0 || r >= rows ? -1 : r * cols + c;
  };

  /* Every point of a grid of `cell` over the building, at least its middle, in the building's own axes. */
  const eachPoint = (obb: OBB, visit: (index: number) => void): number => {
    const [ux, uy] = obb.axisU;
    const nu = Math.max(1, Math.round((2 * obb.halfU) / cell));
    const nv = Math.max(1, Math.round((2 * obb.halfV) / cell));
    for (let i = 0; i < nu; i++)
      for (let j = 0; j < nv; j++) {
        const u = ((i + 0.5) / nu - 0.5) * 2 * obb.halfU;
        const v = ((j + 0.5) / nv - 0.5) * 2 * obb.halfV;
        visit(indexAt(obb.center[0] + ux * u - uy * v, obb.center[1] + uy * u + ux * v));
      }
    return nu * nv;
  };

  const shares = (obb: OBB): GroundShares => {
    let read = 0;
    let open = 0;
    let built = 0;
    const points = eachPoint(obb, (k) => {
      const state = k < 0 ? UNREAD : cells[k]!;
      if (state !== UNREAD) read++;
      if (state === OPEN) open++;
      if (state === BUILT) built++;
    });
    return { read: read / points, open: open / points, built: built / points };
  };

  const isRead = (obb: OBB): boolean => shares(obb).read >= GROUND.read;

  return {
    grid: { origin: [x0, y0], cell, cols, rows },
    at: (p) => {
      const k = indexAt(p[0], p[1]);
      return k < 0 ? 'unread' : STATES[cells[k]!]!;
    },
    stateOf: (col, row) => STATES[cells[row * cols + col]!]!,
    surelyBuilt: (col, row) => sure[row * cols + col] === 1,
    centreOf: (col, row) => [x0 + (col + 0.5) * cell, y0 + (row + 0.5) * cell],
    shares,
    counts: () => {
      const counts = { unread: 0, open: 0, built: 0, hidden: 0 };
      for (const state of cells) counts[STATES[state]!]++;
      return counts;
    },
    isRead,
    showsOpen: (obb) => isRead(obb) && shares(obb).open > GROUND.rearOpen,
    showsBuilt: (obb) => isRead(obb) && shares(obb).built >= GROUND.plotBuilt,
    roofedThrough: (obb) => isRead(obb) && shares(obb).built >= GROUND.through,
  };
}

export const WOODCUT_GROUND: WoodcutGround = parseWoodcutGround(committed);
